/**
 * Instant Search API Service
 *
 * Handles all HTTP requests for the ISW.
 *
 * **Migration from Legacy**:
 * - Legacy: Functions in `api/search.js` (getSuggestionSearch, getRedirects, getProductsDetail, etc.)
 * - New: Injectable API service with proper typing and error handling
 *
 * @module instant-search.api
 */

import type { Analytics } from '@boost/widget-integration-tae/lib';
import { Injectable } from 'framework/di';

import type { ProductItem } from '../../types';
import { BoostAPI } from '../app/app.api';
import { AppService } from '../app/app.service';
import { B2BService } from '../b2b/b2b.service';
import { PlatformLoader } from '../platform/platform.loader';
import type {
  SearchSettings,
  SuggestionRequestParams,
  SuggestionResponse,
} from './instant-search.types';
import { RecentSearchService } from './recent-search.service';

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY_QUERY_STRING = 'boost-sd-query-string';

// =============================================================================
// Types
// =============================================================================

/**
 * Response metadata from API responses.
 */
interface ApiResponseMeta {
  /** Request ID for analytics tracking */
  rid?: string;
  /** Money format string */
  money_format?: string;
  /** Money format with currency code */
  money_format_with_currency?: string;
  /** Currency code */
  currency?: string;
}

/**
 * Redirect data item from the API.
 */
interface RedirectItem {
  /** Terms that trigger the redirect */
  terms: string[];
  /** URL to redirect to */
  redirect_to: string;
}

/**
 * Redirects API response shape.
 */
interface RedirectsApiResponse {
  /** Redirect data items */
  data?: RedirectItem[];
  /** Response metadata */
  meta?: ApiResponseMeta;
}

/**
 * Products API response shape.
 */
interface ProductsApiResponse {
  /** Products array */
  products?: ProductItem[];
  /** Response metadata */
  meta?: ApiResponseMeta;
}

/**
 * Suggestion API response with full data.
 * Uses Omit to override the required meta property from SuggestionResponse.
 */
interface SuggestionApiResponse extends Omit<SuggestionResponse, 'meta'> {
  /** Response metadata (optional in raw API response) */
  meta?: ApiResponseMeta & {
    rid: string;
  };
}

/**
 * Template API response shape.
 */
interface TemplateApiResponse {
  /** Map of template IDs to HTML content */
  templates?: Record<string, string>;
}

/**
 * Window extensions for custom params and Shopify data.
 */
interface WindowWithShopify {
  /** Custom API parameters */
  boostSdCustomParams?: Record<string, string>;
  /** Widget integration instance */
  boostWidgetIntegration?: {
    generateUuid: () => string;
    integration?: {
      languages?: string;
    };
  };
  /** Shopify global object */
  Shopify?: {
    currency?: {
      rate?: number;
      active?: string;
    };
    country?: string;
  };
}

/**
 * API service for instant search operations.
 *
 * Technical Support teams can extend this service for custom API behavior:
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('InstantSearchAPI', (InstantSearchAPI) => {
 *   return class CustomInstantSearchAPI extends InstantSearchAPI {
 *     // Add custom headers
 *     protected buildHeaders() {
 *       return {
 *         ...super.buildHeaders(),
 *         'X-Custom-Header': 'value'
 *       };
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class InstantSearchAPI extends BoostAPI {
  /** Enable debug logging */
  protected debugMode = false;

  constructor(
    protected platformLoader: PlatformLoader,
    protected appService: AppService,
    protected b2bService: B2BService,
    protected recentSearchService: RecentSearchService
  ) {
    super(platformLoader);
    this.config.baseURL = this.appService.TAEAppConfig.searchUrl;
  }

  // ===========================================================================
  // Configuration Getters
  // ===========================================================================

  /**
   * Get the base search API URL.
   */
  protected get baseUrl(): string {
    return this.config.baseURL || this.appService.TAEAppConfig.searchUrl || '';
  }

  /**
   * Get the template API URL.
   */
  protected get templateUrl(): string {
    return this.appService.TAEAppConfig.templateUrl || '';
  }

  /**
   * Get the tenant/shop domain.
   */
  protected get shopDomain(): string {
    return this.appService.TAEAppConfig.shop?.domain || '';
  }

  /**
   * Get the current locale.
   */
  protected get locale(): string {
    return this.platformLoader.platform.localization?.locale || 'en';
  }

  /**
   * Get B2B configuration.
   */
  protected get b2bConfig(): { enabled: boolean; companyLocationId?: string } {
    const appConfig = this.appService.TAEAppConfig as {
      b2b?: {
        enabled: boolean;
        current_company_id?: number;
        current_location_id?: number;
      };
    };
    const b2b = appConfig.b2b;

    if (!b2b?.enabled) {
      return { enabled: false };
    }

    return {
      enabled: true,
      companyLocationId: `${b2b.current_company_id}_${b2b.current_location_id}`,
    };
  }

  /**
   * Get search settings from app config.
   */
  protected get searchSettings(): Partial<SearchSettings> {
    const appConfig = this.appService.TAEAppConfig as {
      searchSettings?: SearchSettings;
    };
    return appConfig.searchSettings || {};
  }

  // ===========================================================================
  // URL and Parameter Building
  // ===========================================================================

  /**
   * Build API URL for external Boost search service.
   *
   * NOTE: The locale is passed as a query parameter (`&locale=en`) via
   * `addCurrencyParams()` / `addLocalizationParams()`
   *
   * @param endpoint - API endpoint (e.g. '/suggest', '/products', '/redirects')
   * @returns Full API URL
   */
  protected buildApiUrl(endpoint: string): string {
    return `${this.baseUrl}${endpoint}`;
  }

  /**
   * Build query parameters for API requests.
   *
   * **Legacy**: buildQueryParams() in api/search.js
   * **New**: Method on API service with better typing
   *
   * Handles:
   * - Array values (appended as multiple params)
   * - Custom params from window.boostSdCustomParams
   * - Customer ID from platform
   *
   * @param params - Parameter object
   * @returns URLSearchParams instance
   */
  protected buildQueryParams(
    params: Record<string, unknown>,
    options: { includeCustomParams?: boolean } = {}
  ): URLSearchParams {
    const { includeCustomParams = true } = options;
    const queryParams = new URLSearchParams();
    queryParams.sort();

    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue;

      if (Array.isArray(value)) {
        value.forEach((item) => queryParams.append(key, String(item)));
      } else {
        queryParams.set(key, String(value));
      }
    }

    // Add custom parameters from window.boostSdCustomParams
    const windowExt = window as unknown as WindowWithShopify;
    const customParams = includeCustomParams ? windowExt.boostSdCustomParams : undefined;
    if (customParams && typeof customParams === 'object') {
      for (const [key, value] of Object.entries(customParams)) {
        queryParams.set(key, value);
      }
    }

    return queryParams;
  }

  /**
   * Build query parameters enriched with shared localization, customer, and B2B data.
   */
  protected buildRequestParams(
    params: Record<string, unknown>,
    options: { includeCustomParams?: boolean; includeB2B?: boolean } = {}
  ): URLSearchParams {
    const { includeCustomParams = true, includeB2B = true } = options;
    let requestParams = this.buildRequestBody(params, { includeB2B });

    const windowExt = window as unknown as WindowWithShopify;
    const shopifyCountry = windowExt.Shopify?.country;
    if (shopifyCountry && !requestParams.country) {
      requestParams = {
        ...requestParams,
        country: shopifyCountry,
      };
    }

    return this.buildQueryParams(requestParams, { includeCustomParams });
  }

  /**
   * Build request payload enriched with shared localization, customer, and optional B2B data.
   */
  protected buildRequestBody(
    params: Record<string, unknown>,
    options: { includeB2B?: boolean } = {}
  ): Record<string, unknown> {
    const { includeB2B = true } = options;
    let requestParams = this.addLocalizationParams(params) as Record<string, unknown>;
    requestParams = this.addCurrencyParams(requestParams) as Record<string, unknown>;
    requestParams = this.addCustomerIdParam(requestParams) as Record<string, unknown>;

    if (includeB2B) {
      requestParams = this.b2bService.addB2BRequestParams(requestParams) as Record<string, unknown>;
    }

    return requestParams;
  }

  /**
   * Build request headers.
   * Override to add custom headers.
   */
  protected buildHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Build the raw request parameters object for a suggestion API call.
   *
   * Primary extension point for TS teams who need to inject custom search
   * parameters, override default values, or add shop-specific query flags
   * without re-implementing the full `getSuggestions()` method.
   *
   * Called by `getSuggestions()` before `buildQueryParams()` serialises to URL.
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchAPI', (InstantSearchAPI) => {
   *   return class CustomAPI extends InstantSearchAPI {
   *     protected buildWidgetRequestParams(params, settings, customParams) {
   *       const base = super.buildWidgetRequestParams(params, settings, customParams);
   *       // Always include a custom collection filter for this shop
   *       return { ...base, collection: 'frontpage' };
   *     }
   *   };
   * });
   * ```
   *
   * @param params - Suggestion request params (query, limits, skip, etc.)
   * @param settings - Resolved search settings (already processed by the service)
   * @param customParams - Optional caller-supplied params merged last
   * @returns Plain object ready for `buildQueryParams()` serialisation
   */
  protected buildWidgetRequestParams(
    params: SuggestionRequestParams,
    settings: Partial<SearchSettings>,
    customParams?: Record<string, unknown>
  ): Record<string, unknown> {
    // Base parameters
    let baseParams: Record<string, unknown> = {
      t: Date.now(),
      shop: this.shopDomain,
      q: this.stripHtml(params.q),
      re_run_if_typo: true,
      event_type: 'suggest',
    };

    // Add suggestion block limits from settings
    const suggestionBlocks = settings.suggestionBlocks;
    if (Array.isArray(suggestionBlocks)) {
      suggestionBlocks.forEach((block) => {
        const limitKey = `${block.type.slice(0, -1)}_limit`;
        baseParams[limitKey] =
          block.limit ||
          block.number ||
          params[`${block.type.slice(0, -1)}_limit` as keyof SuggestionRequestParams];
      });
    } else {
      // Fallback to params limits
      if (params.product_limit !== undefined) baseParams.product_limit = params.product_limit;
      if (params.suggestion_limit !== undefined)
        baseParams.suggestion_limit = params.suggestion_limit;
      if (params.collection_limit !== undefined)
        baseParams.collection_limit = params.collection_limit;
      if (params.page_limit !== undefined) baseParams.page_limit = params.page_limit;
    }

    // Add skip parameter
    if (params.skip && params.skip.length > 0) {
      baseParams.skip = params.skip;
    }

    // Recent search terms — sent for scoped/popular suggestions
    const recentSearches = this.recentSearchService.getRecentSearches();
    if (recentSearches.length > 0) {
      baseParams.recent_search = recentSearches.map((r) => r.title).join(',');
    }

    // Suggestion mode (controls scoped/narrowing suggestions)
    if (settings.suggestionMode) {
      baseParams.suggestionMode = settings.suggestionMode;
    }

    // Fields to skip in search
    if (settings.skipFields && settings.skipFields.length > 0) {
      baseParams.skipFields = settings.skipFields;
    }

    // Did-you-mean suggestion limit
    if (settings.suggestionDymLimit) {
      baseParams.dym_limit = settings.suggestionDymLimit;
    }

    // Fuzzy search (only sent when disabled, defaults to true)
    if (settings.enableFuzzySearch !== undefined && settings.enableFuzzySearch !== true) {
      baseParams.fuzzy = settings.enableFuzzySearch;
    }

    // Reduce minimum match
    if (settings.reduceMinMatch !== undefined && settings.reduceMinMatch !== false) {
      baseParams.reduce_min_match = settings.reduceMinMatch;
    }

    // Full minimum match
    if (settings.fullMinMatch) {
      baseParams.full_min_match = true;
    }

    // Plus character search
    if (settings.enablePlusCharacterSearch) {
      baseParams.enable_plus_character_search = true;
    }

    // Default result (only sent when disabled)
    if (settings.enableDefaultResult !== undefined && !settings.enableDefaultResult) {
      baseParams.enable_default_result = false;
    }

    // Show variants as products
    if (settings.showVariantsAsProduct) {
      baseParams.variants_as_products = true;
    }

    // Product availability filter
    if (settings.productAvailable) {
      baseParams.product_available = true;
    }

    // Merge caller-supplied custom parameters last so they can override defaults
    if (customParams && Object.keys(customParams).length > 0) {
      baseParams = { ...baseParams, ...customParams };
    }

    return baseParams;
  }

  /**
   * Handle a raw suggestion API response before returning it to the service.
   *
   * Delegates to `enrichWidgetResponse()`, giving TS teams a clean hook to
   * intercept and modify results without overriding the entire `getSuggestions()` method.
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchAPI', (InstantSearchAPI) => {
   *   return class CustomAPI extends InstantSearchAPI {
   *     protected handleWidgetResponse(response) {
   *       // Log every API response for diagnostics
   *       console.log('[ISW] response rid:', response.meta?.rid);
   *       return super.handleWidgetResponse(response);
   *     }
   *   };
   * });
   * ```
   *
   * @param response - Raw suggestion response normalised from the API payload
   * @returns Enriched suggestion response
   */
  protected handleWidgetResponse(response: SuggestionResponse): SuggestionResponse {
    return this.enrichWidgetResponse(response);
  }

  /**
   * Enrich a suggestion response with shop-specific data.
   *
   * Called by `handleWidgetResponse()`. Override this method to re-rank products,
   * inject promotions, append custom metadata, or pin specific results.
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchAPI', (InstantSearchAPI) => {
   *   return class CustomAPI extends InstantSearchAPI {
   *     protected enrichWidgetResponse(response) {
   *       // Inject a sponsored product at position 0
   *       return {
   *         ...response,
   *         products: [sponsoredProduct, ...(response.products ?? [])],
   *       };
   *     }
   *   };
   * });
   * ```
   *
   * @param response - Suggestion response to enrich
   * @returns Enriched suggestion response (identity by default)
   */
  protected enrichWidgetResponse(response: SuggestionResponse): SuggestionResponse {
    return response;
  }

  // ===========================================================================
  // Helper Methods
  // ===========================================================================

  /**
   * Log debug messages when debug mode is enabled.
   *
   * @param message - Message to log
   * @param data - Optional data to include
   */
  protected log(message: string, data?: unknown): void {
    if (this.debugMode) {
      if (data !== undefined) {
        this.appService.logger.debug(`[InstantSearchAPI] ${message}`, data);
      } else {
        this.appService.logger.debug(`[InstantSearchAPI] ${message}`);
      }
    }
  }

  /**
   * Strip HTML tags from text.
   *
   * @param text - Text to strip
   * @returns Text without HTML
   */
  protected stripHtml(text: string): string {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.body.textContent || text;
  }

  /**
   * Save query string to localStorage.
   *
   * @param query - Search query
   */
  protected saveQueryString(query: string): void {
    try {
      localStorage.setItem(STORAGE_KEY_QUERY_STRING, JSON.stringify(`q=${this.stripHtml(query)}`));
    } catch {
      // Silently fail - localStorage may be unavailable
    }
  }

  /**
   * Execute a JSON request with shared error handling.
   */
  protected async requestJson<T>(url: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(url, init);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as T;
  }

  // ===========================================================================
  // Public API Methods
  // ===========================================================================

  /**
   * Enable or disable debug mode for API logging.
   *
   * @param enabled - Whether to enable debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Fetch search suggestions.
   *
   * **Legacy**: getSuggestionSearch(context, query, customizeSearchParams)
   * **New**: getSuggestions(params, searchSettings?, customParams?) with typed response
   *
   * Features migrated from legacy:
   * - Timestamp cache busting (t param)
   * - Did-you-mean suggestions (re_run_if_typo)
   * - Suggestion blocks with limits
   * - Recent search integration
   * - Fuzzy search options
   * - Product availability filtering
   * - B2B support
   * - Locale/currency params
   *
   * @param params - Suggestion request parameters
   * @param searchSettings - Optional search settings override
   * @param customParams - Optional custom parameters to merge
   * @returns Suggestion response
   */
  async getSuggestions(
    params: SuggestionRequestParams,
    searchSettings?: Partial<SearchSettings>,
    customParams?: Record<string, unknown>,
    signal?: AbortSignal
  ): Promise<SuggestionResponse> {
    const settings = searchSettings || this.searchSettings;

    this.log('getSuggestions called', { query: params.q, settings });

    try {
      // Build request parameters via the overridable extension hook
      const baseParams = this.buildWidgetRequestParams(params, settings, customParams);

      const queryParams = this.buildRequestParams(baseParams);

      // Make the request
      const url = `${this.buildApiUrl('/suggest')}?${queryParams.toString()}`;
      this.log('Request URL', url);

      const data = await this.requestJson<SuggestionApiResponse>(url, {
        method: 'GET',
        signal,
      });

      // Save query string to localStorage for analytics
      this.saveQueryString(params.q);

      this.log('Response received', { query: data.query, productCount: data.total_product });

      const requestId = typeof data.meta?.rid === 'string' ? data.meta.rid.trim() : '';
      const hasBundles = Array.isArray(data.bundles) && data.bundles.length > 0;
      if (requestId && hasBundles) {
        this.appService
          .getTAEModule<Analytics>('Analytics')
          ?.saveRequestId('suggest', requestId, data.bundles);
      }

      // Ensure meta property exists for type safety — then pass through the
      // response enrichment hook so TS teams can decorate results with shop-specific data.
      return this.handleWidgetResponse({
        ...data,
        meta: data.meta || { rid: '' },
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw error; // Re-throw abort errors to allow proper cancellation handling
      }
      this.appService.logger.error('Failed to fetch instant search suggestions', error);
      // Return minimal response with query to allow graceful degradation
      return {
        all_empty: true,
        query: this.stripHtml(params.q),
        correct_query: '',
        did_you_mean: [],
        products: [],
        suggestions: [],
        collections: [],
        pages: [],
        total_product: 0,
        meta: { rid: '' },
      };
    }
  }

  /**
   * Fetch redirect mappings for search terms.
   *
   * **Legacy**: getRedirects(context, terms)
   * **New**: getRedirects(terms?) with typed response
   *
   * Features migrated from legacy:
   * - Multiple terms support
   * - B2B support
   * - Locale params
   * - Proper response transformation
   *
   * @param terms - Optional array of search terms to check
   * @returns Map of search terms to redirect URLs
   */
  async getRedirects(terms: string[] = []): Promise<Record<string, string>> {
    this.log('getRedirects called', { terms });

    try {
      // Build base parameters
      const baseParams: Record<string, unknown> = {
        t: Date.now(),
        shop: this.shopDomain,
        build_filter_tree: true,
        terms,
        limit: 0, // Get all redirects
      };

      const queryParams = this.buildRequestParams(baseParams);

      // Make the request
      const url = `${this.buildApiUrl('/redirects')}?${queryParams.toString()}`;
      this.log('Request URL', url);

      const responseData = await this.requestJson<RedirectsApiResponse>(url, {
        method: 'GET',
      });

      // Transform response to term -> URL map
      const redirects: Record<string, string> = {};
      responseData.data?.forEach((redirect) => {
        redirect.terms?.forEach((term) => {
          redirects[term] = redirect.redirect_to;
        });
      });

      this.log('Redirects parsed', { count: Object.keys(redirects).length });

      return redirects;
    } catch (error) {
      this.appService.logger.error('Failed to fetch instant search redirects', error);
      return {};
    }
  }

  /**
   * Fetch products by IDs.
   *
   * **Legacy**: getProductsDetail(context, ids)
   * **New**: getProductsById(ids, options?) with typed response
   *
   * Features migrated from legacy:
   * - Product availability filtering
   * - B2B support
   * - Locale params
   *
   * @param ids - Array of product IDs
   * @param options - Optional request options
   * @returns Array of products
   */
  async getProductsById(
    ids: string[],
    options?: { productAvailable?: boolean }
  ): Promise<ProductItem[]> {
    if (!ids || ids.length === 0) {
      this.log('getProductsById called with empty ids');
      return [];
    }

    this.log('getProductsById called', { ids, options });

    try {
      const baseParams: Record<string, unknown> = {
        shop: this.shopDomain,
        event_type: 'init',
        ids,
      };
      const productAvailable = options?.productAvailable ?? this.searchSettings.productAvailable;
      if (productAvailable) {
        baseParams.product_available = productAvailable;
      }
      const queryParams = this.buildRequestParams(baseParams);
      const url = `${this.buildApiUrl('/products')}?${queryParams.toString()}`;
      const data = await this.requestJson<ProductsApiResponse | ProductItem[]>(url, {
        method: 'GET',
      });
      const products = Array.isArray(data) ? data : data.products || [];
      return products;
    } catch (error) {
      this.appService.logger.error('Failed to fetch instant search products by ID', error);
      return [];
    }
  }

  /**
   * Fetch products by handles.
   *
   * **Legacy**: getProductsDetailByHandle(context, handle, searchQuery)
   * **New**: getProductsByHandle(handles, options?) with typed response
   *
   * Features migrated from legacy:
   * - Single or multiple handles support
   * - Product availability filtering
   * - B2B support
   * - POST method for larger payloads
   *
   * @param handles - Array of product handles
   * @param options - Optional request options
   * @returns Array of products
   */
  async getProductsByHandle(
    handles: string[],
    options?: { query?: string; productAvailable?: boolean }
  ): Promise<ProductItem[]> {
    if (!handles || handles.length === 0) {
      this.log('getProductsByHandle called with empty handles');
      return [];
    }

    this.log('getProductsByHandle called', { handles, options });

    try {
      // Build request body for POST
      const body: Record<string, unknown> = {
        shop: this.shopDomain,
        handle: handles.length === 1 ? handles[0] : handles,
        event_type: 'init',
      };

      const productAvailable = options?.productAvailable ?? this.searchSettings.productAvailable;
      if (productAvailable) {
        body.product_available = productAvailable;
      }

      const requestBody = this.buildRequestBody(body);

      // Make the POST request
      const url = this.buildApiUrl('/products');
      this.log('Request URL (POST)', url);

      const data = await this.requestJson<ProductsApiResponse | ProductItem[]>(url, {
        method: 'POST',
        headers: this.buildHeaders(),
        body: JSON.stringify(requestBody),
      });

      // API may return a plain array or { products: [...] } — handle both
      const products = Array.isArray(data) ? data : data.products || [];

      this.log('Products fetched by handle', { count: products.length });

      return products;
    } catch (error) {
      this.appService.logger.error('Failed to fetch instant search products by handle', error);
      return [];
    }
  }

  /**
   * Fetch ISW template by ID.
   *
   * **Legacy**: getTemplateByIds() in api/template.js
   * **New**: getTemplate(templateId) with proper typing
   *
   * Query params must match the legacy format exactly:
   *   - `shop`        – shop domain
   *   - `theme_lib`   – theme name lib (NOT `theme`)
   *   - `widgets[]`   – `name:id` format, bracket suffix required (NOT `ids`, NOT `widgets`)
   *
   * NOTE: This endpoint does NOT accept `customer_id` or custom boost params,
   * so we build URLSearchParams directly instead of using `this.buildQueryParams()`.
   *
   * @param templateId - Template ID to fetch (from `templateMetadata.instantSearchWidget`)
   * @returns Template HTML string (empty string on failure)
   */
  async getTemplate(templateId: string): Promise<string> {
    if (!templateId) {
      this.appService.logger.warn('Instant search template ID is required');
      return '';
    }

    this.log('getTemplate called', { templateId });

    // --- localStorage cache (mirrors api/template.js) ---
    const CACHE_KEY = `WIDGET:${templateId}`;
    const EXPIRED_MS = 86_400_000; // 1 day
    const themeUpdatedAt = (
      this.appService.TAEAppConfig.templateMetadata as Record<string, unknown>
    )?.updatedAt as number | undefined;

    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { time, template } = JSON.parse(cached) as { time: number; template: string };
        const isExpired = Date.now() - time > EXPIRED_MS;
        const isOutdated = themeUpdatedAt !== undefined && time / 1000 < themeUpdatedAt;
        if (!isExpired && !isOutdated) {
          this.log('Template loaded from cache', { templateId });
          return template;
        }
        localStorage.removeItem(CACHE_KEY);
      }
    } catch {
      // localStorage unavailable — proceed with network fetch
    }

    // --- Network fetch ---
    try {
      const themeNameLib = (
        this.appService.TAEAppConfig.templateMetadata as Record<string, unknown>
      )?.themeNameLib as string | undefined;

      // Build params directly — must NOT include customer_id / boostSdCustomParams
      // because the template endpoint rejects unknown params with HTTP 400.
      const queryParams = new URLSearchParams();
      queryParams.set('shop', this.shopDomain);
      if (themeNameLib) queryParams.set('theme_lib', themeNameLib);
      // Legacy format: "widgets[]=name:id" (the [] suffix is required by the server)
      queryParams.append('widgets[]', `instantSearchWidget:${templateId}`);

      const url = `${this.templateUrl}?${queryParams.toString()}`;
      this.log('Request URL', url);

      const data = await this.requestJson<TemplateApiResponse>(url, { method: 'GET' });
      const template = data.templates?.[templateId] || '';

      this.log('Template fetched', { length: template.length });

      // Persist to localStorage cache
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), template }));
      } catch {
        // Quota exceeded — clear all WIDGET: keys and retry once
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k?.startsWith('WIDGET:')) localStorage.removeItem(k);
        }
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), template }));
        } catch {
          // ignore
        }
      }

      return template;
    } catch (error) {
      this.appService.logger.error('Failed to fetch instant search template', error);
      return '';
    }
  }
}
